// ========================================
// pages/ForgotPassword.js - Request Password Reset
// ========================================
// PROJECT: Agyapadie Vault - Digital Inheritance Platform
// LAST MODIFIED: April 12, 2026
// 
// PURPOSE: Allow users to request a password reset email
// 
// FLOW:
// 1. User enters email address
// 2. System sends reset link to email (if account exists)
// 3. User clicks link → goes to ResetPassword page
// 
// SECURITY:
// - No indication if email exists (prevents email enumeration)
// - Reset token expires in 1 hour
// - Token is single-use
// ========================================

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/forgot-password`, { email });
      setSuccess(true);
    } catch (err) {
      // For security, don't reveal if email exists or not
      // Just show generic success message
      setSuccess(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="forgot-password-page">
      <div className="forgot-password-card">
        <div className="logo-container">
          <img src="/images/symbols/logo-light.png" alt="Agyapadie Vault" className="logo" />
        </div>
        
        <h1 className="title">Reset Password</h1>
        
        {!success ? (
          <>
            <p className="subtitle">
              Enter your email address and we'll send you a link to reset your password.
            </p>
            
            {error && <div className="error-message">{error}</div>}
            
            <form onSubmit={handleSubmit} className="form">
              <div className="form-group">
                <label className="label">Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input"
                  placeholder="your@email.com"
                  required
                />
              </div>
              
              <button type="submit" className="submit-btn" disabled={loading}>
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
          </>
        ) : (
          <div className="success-message">
            <p>✅ Check your email</p>
            <p>
              If an account exists with that email, you will receive a password reset link shortly.
            </p>
            <Link to="/login" className="back-to-login">← Back to Login</Link>
          </div>
        )}
        
        <div className="footer-links">
          <Link to="/login" className="footer-link">Back to Login</Link>
          <Link to="/register" className="footer-link">Create Account</Link>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .forgot-password-page {
          min-height: 100vh;
          background: #1A1A1A;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }
        
        .forgot-password-card {
          background: white;
          border-radius: 12px;
          padding: 40px;
          max-width: 450px;
          width: 100%;
          box-shadow: 0 10px 30px rgba(0,0,0,0.2);
        }
        
        .logo-container {
          text-align: center;
          margin-bottom: 30px;
        }
        
        .logo {
          height: 50px;
          width: auto;
        }
        
        .title {
          font-size: 24px;
          font-weight: bold;
          color: #1A1A1A;
          text-align: center;
          margin-bottom: 15px;
        }
        
        .subtitle {
          font-size: 14px;
          color: #666;
          text-align: center;
          margin-bottom: 25px;
        }
        
        .form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        
        .form-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        
        .label {
          font-size: 14px;
          font-weight: bold;
          color: #1A1A1A;
        }
        
        .input {
          padding: 12px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 14px;
          width: 100%;
          box-sizing: border-box;
        }
        
        .input:focus {
          outline: none;
          border-color: #FFD700;
        }
        
        .submit-btn {
          padding: 14px;
          background: #FFD700;
          color: #1A1A1A;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.3s;
        }
        
        .submit-btn:hover {
          background: #B8860B;
        }
        
        .submit-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .error-message {
          background: #fee2e2;
          border: 1px solid #ef4444;
          color: #dc2626;
          padding: 12px;
          border-radius: 8px;
          margin-bottom: 20px;
          text-align: center;
        }
        
        .success-message {
          text-align: center;
          margin-bottom: 25px;
        }
        
        .success-message p:first-child {
          font-size: 48px;
          margin-bottom: 15px;
        }
        
        .success-message p:last-of-type {
          color: #666;
          margin-bottom: 20px;
        }
        
        .back-to-login {
          display: inline-block;
          color: #FFD700;
          text-decoration: none;
          font-weight: bold;
        }
        
        .footer-links {
          display: flex;
          justify-content: center;
          gap: 20px;
          margin-top: 25px;
          padding-top: 20px;
          border-top: 1px solid #eee;
        }
        
        .footer-link {
          color: #2E7D32;
          text-decoration: none;
          font-size: 14px;
        }
        
        .footer-link:hover {
          text-decoration: underline;
        }
        
        @media (max-width: 480px) {
          .forgot-password-card {
            padding: 25px;
          }
          
          .title {
            font-size: 20px;
          }
        }
      `}} />
    </div>
  );
};

export default ForgotPassword;